import React from "react";
import { Alert, Autocomplete, Box, Button, Chip, FormControlLabel, IconButton, LinearProgress, Link, Switch, Tab, Tabs,
         TextField, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadIcon from "@mui/icons-material/Upload";
import EventIcon from "@mui/icons-material/Event";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import api from "../../Auth/Api";
import globals from "../../Globals";
import { navigate, replaceDeep } from "../../Layout/Router";
import { subscribePoll } from "../../Layout/Poller";
import { PageContainer, SectionHeader, SelectorBar, SelectField, FormDialog, DataTable } from "../../Layout/Common";
import CountryPicker, { countryName } from "../../Layout/CountryPicker";
import { ALL, ID_RE, FIXED, ellipsis, mono, Scroll, canEdit, canReadVersioning, asDate, utc, utcFull, toUtcInput, fromUtcInput, span,
         slotStatus, SlotChip, sortSlots, emptySlot, verLabel, verRange, clientPatchesOf, parseClient, slotVersionOptions, PlatformPicker,
         countryOptions, formatSize, checkBundleZip, UPLOAD_STATUS, uploadStatus, int } from "./liveops";

// LiveOps → Offers (Evil Tower's live offers, the backend group `liveoffers`): the DEFINITIONS (a popup selling
// real-money products - a buy-all and/or up to 8 tiers -, a purchase limit per slot, the requirements the client
// checks, its Unity bundle) and the SCHEDULE (slots: a UTC window + targeting by platform, client version and
// country; purchase limits key on the slot). Everything is per branch; every write bumps the branch's offers stamp,
// so players pick a change up at their next session sync. View needs `#liveops`, edits `liveops`. Dates are UTC.
// Deep link: /<game>/liveops/offers/<definitions|schedule>[/<offerId|slotId>][/<branch> when not Development].
const TABS = [["definitions", "Definitions"], ["schedule", "Schedule"]];
const liveoffers = (description, parts, options) => api.group("liveoffers")(description, parts, options);
const REFRESH_MS = 60000;
const UPLOAD_TIMEOUT = 300000;   // the zip goes to the CDN bundle by bundle
const MAX_TIERS = 8;
const WORLDS = [{ value: ALL, label: "Any world" }, "plains", "mountain", "forest", "tundra"];

// ---------- the store catalogue (versioning get-docs store: the typed StoreCatalog, packs + products by id) ----------
const entriesOf = (x) => {
    if (!x) return [];
    if (Array.isArray(x)) return x.map(e => (Array.isArray(e) ? [e[0], e[1]] : e && typeof e === "object" && "k" in e && "v" in e ? [e.k, e.v] : [null, e]));
    return typeof x === "object" ? Object.entries(x) : [];
};
function readCatalogue(data) {
    const products = {}, packs = {};
    for (const [k, p] of entriesOf(data?.products)) { const id = p?.productId || k; if (id) products[id] = p; }
    for (const [k, p] of entriesOf(data?.packs)) { const id = p?.id || k; if (id) packs[id] = { ...p, grants: Object.fromEntries(entriesOf(p?.grants).filter(([g]) => g != null)) }; }
    return { products, packs };
}
const priceText = (p) => (p.currency === "money" ? `$${Number(p.price ?? 0).toFixed(2)}` : p.currency === "gems" ? `${p.price} gems` : p.currency || "?");
const grantsText = (pack) => {
    const g = Object.entries(pack?.grants ?? {});
    return g.length ? g.map(([k, v]) => `${v} ${k}`).join(" · ") : "grants nothing";
};
const productLine = (p, cat) => [
    priceText(p), p.variant || "regular", p.offer ? `offer ${p.offer}${p.tier ? " / " + p.tier : ""}` : null,
    `${p.pack || "no pack"}: ${grantsText(cat.packs[p.pack])}`, p.active ? null : "INACTIVE", p.currency === "money" ? null : "not a money product",
].filter(Boolean).join(" · ");
const byTier = (a, b) => (a ?? "").localeCompare(b ?? "", undefined, { numeric: true });
// The money products an offer can sell, active first, grouped by the catalogue's offer / tier
const offerableIds = (cat) => Object.values(cat.products).filter(p => p.currency === "money")
    .sort((a, b) => (a.active === b.active ? 0 : a.active ? -1 : 1) || byTier(a.offer, b.offer) || byTier(a.tier, b.tier) || a.productId.localeCompare(b.productId))
    .map(p => p.productId);
// The catalogue's own rows for an offer id (its `offer` column): the buyall variant + the tiers in tier order
const catalogueOffer = (cat, offerId) => {
    const id = (offerId ?? "").trim().toLowerCase();
    if (!cat || !id) return null;
    const rows = Object.values(cat.products).filter(p => (p.offer ?? "").toLowerCase() === id && p.currency === "money");
    if (rows.length === 0) return null;
    return {
        buyAll: rows.find(p => p.variant === "buyall")?.productId ?? "",
        tiers: rows.filter(p => p.variant !== "buyall").sort((a, b) => byTier(a.tier, b.tier) || a.productId.localeCompare(b.productId)).map(p => p.productId).slice(0, MAX_TIERS),
    };
};

function ProductInfo({ id, cat }) {
    if (!id || !cat) return null;
    const p = cat.products[id];
    const sx = { ...ellipsis, display: "block", minWidth: 0 };
    if (!p) return <Typography variant="caption" color="warning.main" sx={sx}>not in the store catalogue {cat.source}</Typography>;
    const text = productLine(p, cat);
    return <Typography variant="caption" color={p.active && p.currency === "money" ? "text.secondary" : "warning.main"} sx={sx} title={text}>{text}</Typography>;
}

const PURCHASE_STATE = { granted: "success", pending: "warning", authorized: "info", revoked: "error", refused: "error" };
const emptyOffer = () => ({ offerId: "", title: "", buyAll: "", tiers: [], limit: 1, minWave: 0, world: "", minSessions: 0, autoOpen: true, notifications: true });
const sellsText = (o) => {
    const n = (o.tiers ?? []).length, tiers = `${n} tier${n === 1 ? "" : "s"}`;
    return o.buyAll ? (n ? `buy-all + ${tiers}` : "buy-all only") : tiers;
};
const reqText = (o) => [
    o.minWave ? `wave ${o.minWave}${o.world ? ` in ${o.world}` : ""}` : o.world ? `world ${o.world}` : null,
    o.minSessions ? `${o.minSessions} sessions` : null,
].filter(Boolean).join(" · ") || "none";
const flagsText = (o) => [o.autoOpen ? null : "manual open", o.notifications ? null : "no notifications"].filter(Boolean).join(" · ");

export default class OffersPage extends React.Component {
    loadToken = 0;
    state = {
        tab: "definitions", branch: "Development", data: null, loading: false, error: null,
        versionList: [],       // the versioning list (every branch): the version selects + where the catalogue is
        catalogue: null,       // { products, packs, source } | { error } | null (loading)
        filter: ALL,           // the Schedule tab's offer filter
        offerDialog: null,     // { mode: create | update, offer, busy, error }
        slotDialog: null,      // { mode: create | update, slot, busy, error }
        upload: null,          // { offerId, file, check, checkError, busy, error, result }
        confirm: null,         // { kind: offer | slot, id, label, slots, busy, error }
        preview: null,         // { platform, client, clientText, country, at, busy, error, result }
        purchases: null,       // { slot, rows, error }
    };

    componentDidMount() {
        const deep = globals.route?.deep ?? [];
        const tab = TABS.some(([k]) => k === deep[0]) ? deep[0] : "definitions";
        const rest = deep.slice(1).filter(Boolean);
        const branch = rest.find(d => globals.branches.includes(d)) ?? "Development";
        const openId = rest.find(d => !globals.branches.includes(d)) ?? null;
        // sequenced: two requests in the same millisecond trip the backend's per-device anti-replay
        this.setState({ tab, branch }, () => { this.load(false, openId).finally(this.loadVersions); });
        this.unsubscribe = subscribePoll(() => this.load(true), REFRESH_MS);
    }

    componentWillUnmount() { this.unsubscribe?.(); }

    syncUrl = (id) => replaceDeep([this.state.tab, id ?? "", this.state.branch === "Development" ? "" : this.state.branch]);

    // ---------- loads ----------
    load = (poll = false, openId = null) => {
        const { branch } = this.state;
        const token = ++this.loadToken;
        if (!poll) this.setState({ loading: true, error: null });
        return liveoffers("Loading offers", ["list", branch], { silent: true })
            .then(r => {
                if (token !== this.loadToken) return true;
                this.setState({ data: r && typeof r === "object" ? r : null, loading: false, error: null });
                if (openId && r) this.openDeep(openId, r);
                return true;
            })
            .catch(err => {
                if (token === this.loadToken) this.setState(s => ({ loading: false, error: poll ? s.error : err.message }));
                return false;
            });
    }

    openDeep = (id, r) => {
        if (this.state.tab === "definitions") {
            const offer = (r.offers ?? []).find(o => o.offerId === id);
            if (offer) this.openOffer(offer);
        } else {
            // a slot id opens it; an offer id filters the schedule to that offer
            const slot = (r.slots ?? []).find(s => s.slotId === id);
            if (slot) this.openSlot(slot);
            else if ((r.offers ?? []).some(o => o.offerId === id)) this.setState({ filter: id });
        }
    }

    loadVersions = () => {
        if (!canReadVersioning()) {
            this.setState({ versionList: [], catalogue: { error: "reading the versions and the store catalogue needs the database or players view role" } });
            return Promise.resolve();
        }
        return api.versioning("Loading versions", ["list"], { silent: true })
            .then(list => { this.setState({ versionList: Array.isArray(list) ? list : [] }, this.loadCatalogue); })
            .catch(err => this.setState({ versionList: [], catalogue: { error: err.message } }));
    }

    // The newest version of the branch with a store catalogue applied, at its latest patch
    loadCatalogue = () => {
        const { versionList, branch } = this.state;
        if (!canReadVersioning()) return;
        const bi = globals.branches.indexOf(branch);
        const versions = [...((versionList ?? []).find(b => b.branch === bi)?.versions ?? [])].sort((a, b) => b.version - a.version);
        const row = versions.find(v => (v.docs?.store ?? []).length > 0);
        if (!row) { this.setState({ catalogue: { error: `no store catalogue applied on ${branch}` } }); return; }
        const patch = Math.max(...row.docs.store.map(p => p.patch));
        const { major, minor } = globals.decompactVersion(row.version);
        const source = `${major}.${minor} patch ${patch}`;
        this.setState({ catalogue: null });
        api.versioning("Loading the store catalogue", ["get-docs", "store", globals.compactVersion(major, minor, patch, bi)], { silent: true })
            .then(r => {
                if (this.state.branch !== branch) return;
                const cat = readCatalogue(r?.data);
                this.setState({ catalogue: Object.keys(cat.products).length ? { ...cat, source } : { error: `the store catalogue ${source} lists no products` } });
            })
            .catch(err => { if (this.state.branch === branch) this.setState({ catalogue: { error: err.message } }); });
    }

    changeBranch = (branch) => {
        this.setState({ branch, data: null, filter: ALL, catalogue: null }, () => {
            this.syncUrl();
            this.load().finally(this.loadCatalogue);
        });
    }

    setTab = (_, tab) => this.setState({ tab }, () => this.syncUrl());

    // ---------- offers ----------
    openNewOffer = () => this.setState({ offerDialog: { mode: "create", offer: emptyOffer(), busy: false, error: null } });

    openOffer = (o) => {
        this.setState({ offerDialog: { mode: "update", offer: { ...emptyOffer(), ...o, tiers: [...(o.tiers ?? [])], buyAll: o.buyAll ?? "", world: o.world ?? "" }, busy: false, error: null } });
        this.syncUrl(o.offerId);
    }

    closeOffer = () => { this.setState({ offerDialog: null }); this.syncUrl(); }

    setOffer = (patch) => this.setState(s => ({ offerDialog: s.offerDialog && { ...s.offerDialog, offer: { ...s.offerDialog.offer, ...patch }, error: null } }));

    moveTier = (i, dir) => {
        const tiers = [...this.state.offerDialog.offer.tiers];
        [tiers[i], tiers[i + dir]] = [tiers[i + dir], tiers[i]];
        this.setOffer({ tiers });
    }

    offerProblem(o, create) {
        if (create && !ID_RE.test(o.offerId.trim())) return "The offer id: 1-40 letters, digits, '_' or '-'.";
        const buyAll = o.buyAll.trim();
        if (o.tiers.length === 0 && !buyAll) return "An offer sells at least one product (tiers or buy-all).";
        if (o.tiers.length > MAX_TIERS) return `At most ${MAX_TIERS} tiers.`;
        if (new Set(o.tiers).size !== o.tiers.length) return "A product is listed twice.";
        if (buyAll && o.tiers.includes(buyAll)) return "The buy-all product cannot also be a tier.";
        return null;
    }

    saveOffer = () => {
        const { offerDialog: d, branch } = this.state;
        const o = d.offer, create = d.mode === "create";
        const problem = this.offerProblem(o, create);
        if (problem) { this.setState({ offerDialog: { ...d, error: problem } }); return; }
        const id = create ? o.offerId.trim() : o.offerId;
        // strict on the backend: exactly these keys
        const payload = {
            title: (o.title ?? "").trim(), buyAll: o.buyAll.trim(), tiers: o.tiers.map(t => t.trim()).filter(Boolean),
            limit: Math.max(1, int(o.limit, 1)), minWave: Math.max(0, int(o.minWave)), world: o.world ?? "", minSessions: Math.max(0, int(o.minSessions)),
            autoOpen: !!o.autoOpen, notifications: !!o.notifications,
        };
        this.setState({ offerDialog: { ...d, busy: true, error: null } });
        liveoffers(create ? `Creating offer ${id}` : `Saving offer ${id}`, [d.mode, branch, id], { payload })
            .then(() => { this.setState({ offerDialog: null }); this.syncUrl(); this.load(); })
            .catch(err => this.setState(s => ({ offerDialog: s.offerDialog && { ...s.offerDialog, busy: false, error: err.message } })));
    }

    scheduleOffer = (o) => this.setState({ tab: "schedule", filter: o.offerId }, () => { this.syncUrl(); this.openNewSlot(o.offerId); });

    // ---------- the bundle ----------
    openUpload = (o) => this.setState({ upload: { offerId: o.offerId, file: null, check: null, checkError: null, busy: false, error: null, result: null } });

    pickZip = (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        const { offerId } = this.state.upload;
        this.setState(s => ({ upload: { ...s.upload, file, check: null, checkError: null, error: null } }));
        checkBundleZip(file, offerId)
            .then(check => this.setState(s => (s.upload?.file === file ? { upload: { ...s.upload, check } } : null)))
            .catch(err => this.setState(s => (s.upload?.file === file ? { upload: { ...s.upload, checkError: err.message || String(err) } } : null)));
    }

    uploadZip = () => {
        const { upload: u, branch } = this.state;
        this.setState({ upload: { ...u, busy: true, error: null } });
        // a File payload carries no JSON body: every parameter is positional
        liveoffers(`Uploading the bundle of ${u.offerId}`, ["upload-bundle", branch, u.offerId], { payload: u.file, timeout: UPLOAD_TIMEOUT })
            .then(result => { this.setState(s => ({ upload: s.upload && { ...s.upload, busy: false, result } })); this.load(); })
            .catch(err => this.setState(s => ({ upload: s.upload && { ...s.upload, busy: false, error: err.message } })));
    }

    // ---------- slots ----------
    openNewSlot = (key) => {
        const { filter, data } = this.state;
        const offers = data?.offers ?? [];
        const pick = key ?? (filter !== ALL && offers.some(o => o.offerId === filter) ? filter : offers[0]?.offerId ?? "");
        this.setState({ slotDialog: { mode: "create", slot: emptySlot(pick), busy: false, error: null } });
    }

    openSlot = (s) => {
        this.setState({ slotDialog: { mode: "update", slot: { ...s, platforms: [...(s.platforms ?? [])], countries: [...(s.countries ?? [])], note: s.note ?? "" }, busy: false, error: null } });
        this.syncUrl(s.slotId);
    }

    closeSlot = () => { this.setState({ slotDialog: null }); this.syncUrl(); }

    setSlot = (patch) => this.setState(s => ({ slotDialog: s.slotDialog && { ...s.slotDialog, slot: { ...s.slotDialog.slot, ...patch }, error: null } }));

    saveSlot = () => {
        const { slotDialog: d, branch } = this.state;
        const s = d.slot;
        const start = asDate(s.start), end = asDate(s.end);
        const problem = !s.key ? "Pick the offer."
            : !start || !end ? "Start and end are required."
            : end <= start ? "The end must be after the start."
            : s.minVersion && s.maxVersion && s.maxVersion < s.minVersion ? "The max version is below the min version." : null;
        if (problem) { this.setState({ slotDialog: { ...d, error: problem } }); return; }
        const payload = {
            key: s.key, start: start.toISOString(), end: end.toISOString(), platforms: s.platforms ?? [],
            minVersion: s.minVersion ?? 0, maxVersion: s.maxVersion ?? 0, countries: s.countries ?? [], enabled: !!s.enabled, note: (s.note ?? "").trim(),
        };
        const create = d.mode === "create";
        this.setState({ slotDialog: { ...d, busy: true, error: null } });
        liveoffers(create ? `Scheduling ${s.key}` : "Saving the slot", create ? ["slot-create", branch] : ["slot-update", branch, s.slotId], { payload })
            .then(() => { this.setState({ slotDialog: null }); this.syncUrl(); this.load(); })
            .catch(err => this.setState(st => ({ slotDialog: st.slotDialog && { ...st.slotDialog, busy: false, error: err.message } })));
    }

    // The slot's version gate options (the shared rule in liveops.js)
    versionOptions(kind, current) { return slotVersionOptions(this.state.versionList, this.state.branch, kind, current); }

    // ---------- deletes ----------
    confirmDelete = () => {
        const { confirm: c, branch } = this.state;
        this.setState({ confirm: { ...c, busy: true, error: null } });
        liveoffers(c.kind === "offer" ? `Deleting offer ${c.id}` : "Deleting the slot", c.kind === "offer" ? ["delete", branch, c.id] : ["slot-delete", branch, c.id])
            .then(() => { this.setState({ confirm: null }); this.load(); })
            .catch(err => this.setState(s => ({ confirm: s.confirm && { ...s.confirm, busy: false, error: err.message } })));
    }

    // ---------- preview / purchases ----------
    openPreview = () => {
        const versions = clientPatchesOf(this.state.versionList, this.state.branch);
        this.setState({ preview: { platform: globals.platformNames[0], client: versions[0]?.value ?? "", clientText: "", country: "", at: "", busy: false, error: null, result: null } });
    }

    previewCompact() {
        const { preview: p, branch } = this.state;
        if (!p) return null;
        return p.clientText.trim() ? parseClient(p.clientText, branch) : (p.client === "" ? null : p.client);
    }

    runPreview = () => {
        const { preview: p, branch } = this.state;
        const compact = this.previewCompact();
        if (compact == null) return;
        const at = fromUtcInput(p.at);
        const parts = ["preview", branch, p.platform, compact];
        // positional: a time without a country passes "-" (matches no country-targeted slot, like no country at all)
        if (p.country || at) parts.push(p.country || "-");
        if (at) parts.push(at);
        this.setState({ preview: { ...p, busy: true, error: null } });
        liveoffers("Previewing", parts, { silent: true })
            .then(result => this.setState(s => ({ preview: s.preview && { ...s.preview, busy: false, result: { ...result, compact } } })))
            .catch(err => this.setState(s => ({ preview: s.preview && { ...s.preview, busy: false, error: err.message, result: null } })));
    }

    openPurchases = (slot) => {
        this.setState({ purchases: { slot, rows: null, error: null } });
        liveoffers("Loading purchases", ["purchases", this.state.branch, slot.slotId], { silent: true })
            .then(rows => this.setState(s => (s.purchases?.slot === slot ? { purchases: { ...s.purchases, rows: Array.isArray(rows) ? rows : [] } } : null)))
            .catch(err => this.setState(s => (s.purchases?.slot === slot ? { purchases: { ...s.purchases, rows: [], error: err.message } } : null)));
    }

    // ---------- render: tables ----------
    renderDefinitions() {
        const { data, loading, branch } = this.state;
        const edit = canEdit();
        const offers = data?.offers ?? [];
        const now = Date.now();
        if (!data) return null;
        if (offers.length === 0) {
            return <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>{loading ? "Loading…" : `No offers on ${branch} yet.${edit ? " Create one with New offer." : ""}`}</Typography>;
        }
        const upcoming = new Set((data.slots ?? []).filter(s => slotStatus(s, now) === "upcoming").map(s => s.key));
        const columns = [
            { id: "offerId", label: "Offer", width: 170, sx: ellipsis, render: o => <span title={o.offerId} style={mono}>{o.offerId}</span> },
            { id: "title", label: "Title", width: 180, sx: ellipsis, render: o => <span title={o.title}>{o.title || "—"}</span> },
            { id: "sells", label: "Sells", width: 150, sx: ellipsis, render: o => (
                <Tooltip title={<Box sx={{ whiteSpace: "pre-line" }}>{[o.buyAll ? `buy-all: ${o.buyAll}` : null, ...(o.tiers ?? []).map((t, i) => `${i + 1}. ${t}`)].filter(Boolean).join("\n")}</Box>}>
                    <span>{sellsText(o)}</span>
                </Tooltip>
            )},
            { id: "bundle", label: "Bundle", width: 170, sx: ellipsis, render: o => (o.bundle ? <span title={o.bundle} style={mono}>{o.bundle}</span> : "—") },
            { id: "limit", label: "Limit", width: 60, align: "right", render: o => o.limit },
            { id: "req", label: "Requirements", width: 200, sx: ellipsis, render: o => {
                const t = reqText(o), f = flagsText(o);
                return <span title={[t, f].filter(Boolean).join(" · ")}>{t}{f && <Typography component="span" variant="caption" color="text.secondary"> · {f}</Typography>}</span>;
            }},
            { id: "slots", label: "Slots", width: 60, align: "right", render: o => o.slots },
            { id: "live", label: "Live", width: 90, render: o => (o.live ? <SlotChip status="live" /> : upcoming.has(o.offerId) ? <SlotChip status="upcoming" /> : "—") },
            { id: "updated", label: "Updated (UTC)", width: 140, render: o => <Tooltip title={`v${o.version} · ${o.updatedBy ?? ""}`}><span>{utc(o.updatedDate)}</span></Tooltip> },
            { id: "actions", label: "", width: 150, align: "right", render: o => (
                <Box sx={{ whiteSpace: "nowrap" }}>
                    <Tooltip title={edit ? "Edit" : "View"}><IconButton size="small" onClick={() => this.openOffer(o)}>{edit ? <EditIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}</IconButton></Tooltip>
                    {edit && <>
                        <Tooltip title="Upload bundle"><IconButton size="small" onClick={() => this.openUpload(o)}><UploadIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Schedule"><IconButton size="small" onClick={() => this.scheduleOffer(o)}><EventIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" onClick={() => this.setState({ confirm: { kind: "offer", id: o.offerId, slots: o.slots, busy: false, error: null } })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </>}
                </Box>
            )},
        ];
        return <DataTable sx={FIXED} columns={columns} rows={offers} getRowKey={o => o.offerId} />;
    }

    renderSchedule(filter) {
        const { data, loading, branch } = this.state;
        const edit = canEdit();
        if (!data) return null;
        const now = Date.now();
        const titles = new Map((data.offers ?? []).map(o => [o.offerId, o.title]));
        const rows = sortSlots((data.slots ?? []).filter(s => filter === ALL || s.key === filter), now);
        if (rows.length === 0) {
            return <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>{loading ? "Loading…" : `Nothing scheduled on ${branch}${filter === ALL ? "" : ` for ${filter}`}.`}</Typography>;
        }
        const columns = [
            { id: "offer", label: "Offer", width: 170, sx: ellipsis, render: s => (
                <span title={titles.has(s.key) ? `${s.key} · ${titles.get(s.key) || ""}` : `${s.key} (no such offer)`} style={mono}>{s.key}</span>
            )},
            { id: "window", label: "Window (UTC)", width: 270, render: s => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, whiteSpace: "nowrap" }}>
                    <SlotChip status={slotStatus(s, now)} />
                    <Tooltip title={`${utcFull(s.start)} → ${utcFull(s.end)}`}><span>{utc(s.start)} → {utc(s.end)}</span></Tooltip>
                </Box>
            )},
            { id: "platforms", label: "Platforms", width: 150, sx: ellipsis, render: s => ((s.platforms ?? []).length
                ? <span title={s.platforms.join(", ")}>{s.platforms.map(p => <Chip key={p} size="small" label={p} sx={{ mr: 0.5, height: 20 }} />)}</span> : "all") },
            { id: "versions", label: "Versions", width: 150, sx: ellipsis, render: s => verRange(s.minVersion, s.maxVersion) },
            { id: "countries", label: "Countries", width: 150, sx: ellipsis, render: s => ((s.countries ?? []).length
                ? <span title={s.countries.map(c => `${countryName(c)} (${c})`).join(", ")}>{s.countries.join(", ")}</span> : "all") },
            { id: "note", label: "Note", width: 200, sx: ellipsis, render: s => <span title={s.note ?? ""}>{s.note ?? ""}</span> },
            { id: "updated", label: "Updated (UTC)", width: 140, render: s => <Tooltip title={s.updatedBy ?? ""}><span>{utc(s.updatedDate)}</span></Tooltip> },
            { id: "actions", label: "", width: 130, align: "right", render: s => (
                <Box sx={{ whiteSpace: "nowrap" }}>
                    <Tooltip title="Purchases"><IconButton size="small" onClick={() => this.openPurchases(s)}><ReceiptLongIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title={edit ? "Edit" : "View"}><IconButton size="small" onClick={() => this.openSlot(s)}>{edit ? <EditIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}</IconButton></Tooltip>
                    {edit && <Tooltip title="Delete"><IconButton size="small" onClick={() => this.setState({ confirm: { kind: "slot", id: s.slotId, label: `${s.key} ${utc(s.start)} → ${utc(s.end)}`, busy: false, error: null } })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>}
                </Box>
            )},
        ];
        return <DataTable sx={FIXED} columns={columns} rows={rows} getRowKey={s => s.slotId} />;
    }

    // ---------- render: dialogs ----------
    renderProductPicker(o, cat, disabled) {
        const options = cat ? offerableIds(cat) : [];
        const filterOptions = (opts, { inputValue }) => {
            const q = inputValue.trim().toLowerCase();
            if (!q) return opts;
            return opts.filter(id => id.toLowerCase().includes(q) || [cat?.products[id]?.offer, cat?.products[id]?.pack].some(x => (x ?? "").toLowerCase().includes(q)));
        };
        const renderOption = (props, id) => {
            const { key, ...rest } = props;
            const p = cat?.products[id];
            return (
                <li key={key} {...rest}>
                    <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                        <Box component="span" sx={mono}>{id}</Box>
                        {p && <Typography variant="caption" color={p.active ? "text.secondary" : "warning.main"}>{productLine(p, cat)}</Typography>}
                    </Box>
                </li>
            );
        };
        const suggestion = catalogueOffer(cat, o.offerId);
        const differs = suggestion && (suggestion.buyAll !== o.buyAll.trim() || suggestion.tiers.join("|") !== o.tiers.join("|"));
        return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                        {cat ? `Products of the store catalogue ${cat.source} (money products listed; any id can be typed).`
                             : `No product list (${this.state.catalogue?.error ?? "loading the store catalogue…"}): type the product ids, Enter adds a tier.`}
                    </Typography>
                    {differs && !disabled && <Button size="small" onClick={() => this.setOffer(suggestion)}>Fill from the catalogue's “{o.offerId.trim()}” rows</Button>}
                </Box>
                <Box>
                    <Autocomplete
                        freeSolo size="small" options={options} disabled={disabled} filterOptions={filterOptions} renderOption={renderOption}
                        value={o.buyAll || null} inputValue={o.buyAll} onChange={(e, v) => this.setOffer({ buyAll: v ?? "" })}
                        onInputChange={(e, v) => this.setOffer({ buyAll: v ?? "" })}
                        renderInput={params => <TextField {...params} label="Buy-all product" placeholder="none" />}
                    />
                    <ProductInfo id={o.buyAll.trim()} cat={cat} />
                </Box>
                <Autocomplete
                    multiple freeSolo autoSelect={!cat} size="small" options={options} disabled={disabled} filterOptions={filterOptions} renderOption={renderOption}
                    value={o.tiers} onChange={(e, v) => this.setOffer({ tiers: [...new Set(v.map(t => String(t).trim()).filter(Boolean))] })}
                    renderValue={(vals, getTagProps) => vals.map((id, i) => { const { key, ...tag } = getTagProps({ index: i }); return <Chip key={key} size="small" label={`${i + 1}. ${id}`} {...tag} />; })}
                    renderInput={params => <TextField {...params} label={`Tiers (in order, at most ${MAX_TIERS})`} placeholder={o.tiers.length ? "" : "add a product"} error={o.tiers.length > MAX_TIERS} />}
                />
                {o.tiers.length > 0 && (
                    <Box component="ol" sx={{ m: 0, pl: 3 }}>
                        {o.tiers.map((id, i) => (
                            <li key={id}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                                    <Box component="span" sx={{ ...mono, flexShrink: 0 }}>{id}</Box>
                                    <Box sx={{ minWidth: 0, flex: 1, display: "flex" }}><ProductInfo id={id} cat={cat} /></Box>
                                    {!disabled && <Box sx={{ whiteSpace: "nowrap" }}>
                                        <IconButton size="small" disabled={i === 0} onClick={() => this.moveTier(i, -1)}><ArrowUpwardIcon fontSize="small" /></IconButton>
                                        <IconButton size="small" disabled={i === o.tiers.length - 1} onClick={() => this.moveTier(i, 1)}><ArrowDownwardIcon fontSize="small" /></IconButton>
                                    </Box>}
                                </Box>
                            </li>
                        ))}
                    </Box>
                )}
            </Box>
        );
    }

    renderOfferDialog() {
        const { offerDialog: d, catalogue, branch } = this.state;
        if (!d) return null;
        const o = d.offer, create = d.mode === "create", edit = canEdit();
        const cat = catalogue?.products ? catalogue : null;
        const badId = create && !!o.offerId && !ID_RE.test(o.offerId.trim());
        const num = (label, field, helperText) => (
            <TextField label={label} type="number" size="small" sx={{ width: 150 }} value={o[field]} disabled={!edit} helperText={helperText}
                       slotProps={{ htmlInput: { min: field === "limit" ? 1 : 0 } }} onChange={e => this.setOffer({ [field]: e.target.value })} />
        );
        return (
            <FormDialog open title={create ? `New offer · ${branch}` : `${edit ? "Edit" : "Offer"} ${o.offerId} · ${branch}`} onClose={this.closeOffer} onConfirm={this.saveOffer}
                        confirmText={create ? "Create" : "Save"} hideConfirm={!edit} cancelText={edit ? "Cancel" : "Close"}
                        confirmDisabled={d.busy || badId} cancelDisabled={d.busy} maxWidth="md" fullWidth>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                    {d.error && <Alert severity="error">{d.error}</Alert>}
                    <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                        <TextField label="Offer id" size="small" value={o.offerId} disabled={!create} error={badId} sx={{ width: 300, flexShrink: 0 }}
                                   helperText={create ? "Letters, digits, _ and -, max 40: also the dialog id and the Unity folder Assets/LiveOps/<id>" : `Unity folder Assets/LiveOps/${o.offerId}`}
                                   onChange={e => this.setOffer({ offerId: e.target.value })} />
                        <TextField label="Title" size="small" fullWidth value={o.title ?? ""} disabled={!edit} helperText="The operator's label"
                                   onChange={e => this.setOffer({ title: e.target.value })} />
                    </Box>
                    {this.renderProductPicker(o, cat, !edit)}
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
                        {num("Limit", "limit", "1 = choose one")}
                        {num("Min wave", "minWave", "0 = none")}
                        <SelectField label="World" size="small" minWidth={160} value={o.world || ALL} options={WORLDS} disabled={!edit}
                                     onChange={v => this.setOffer({ world: v === ALL ? "" : v })} />
                        {num("Min sessions", "minSessions", "0 = none")}
                    </Box>
                    <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                        <FormControlLabel label="Auto open (once per app run)" disabled={!edit}
                                          control={<Switch checked={!!o.autoOpen} onChange={e => this.setOffer({ autoOpen: e.target.checked })} />} />
                        <FormControlLabel label="Notifications (available / ending)" disabled={!edit}
                                          control={<Switch checked={!!o.notifications} onChange={e => this.setOffer({ notifications: e.target.checked })} />} />
                    </Box>
                    {!create && (
                        <Typography variant="body2" color="text.secondary">
                            Bundle: {o.bundle ? <Box component="span" sx={mono}>{o.bundle}</Box> : `none yet - upload the LiveOpsBundleBuild zip of Assets/LiveOps/${o.offerId}`}
                            {" · "}{o.slots ?? 0} slot{o.slots === 1 ? "" : "s"} · v{o.version} · updated {utcFull(o.updatedDate)} by {o.updatedBy || "?"}
                        </Typography>
                    )}
                </Box>
            </FormDialog>
        );
    }

    renderSlotDialog() {
        const { slotDialog: d, data, branch } = this.state;
        if (!d) return null;
        const s = d.slot, create = d.mode === "create", edit = canEdit();
        const offers = data?.offers ?? [];
        const offerOptions = offers.map(o => ({ value: o.offerId, label: o.title ? `${o.offerId} · ${o.title}` : o.offerId }));
        if (s.key && !offers.some(o => o.offerId === s.key)) offerOptions.push({ value: s.key, label: `${s.key} (no such offer)` });
        const start = asDate(s.start), end = asDate(s.end);
        const length = start && end && end > start ? `lasts ${span(end - start)}` : "typed in UTC";
        return (
            <FormDialog open title={create ? `New slot · ${branch}` : `${edit ? "Edit slot" : "Slot"} · ${s.key} · ${branch}`} onClose={this.closeSlot} onConfirm={this.saveSlot}
                        confirmText={create ? "Schedule" : "Save"} hideConfirm={!edit} cancelText={edit ? "Cancel" : "Close"}
                        confirmDisabled={d.busy} cancelDisabled={d.busy} maxWidth="md" fullWidth>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                    {d.error && <Alert severity="error">{d.error}</Alert>}
                    <SelectField label="Offer" size="small" fullWidth value={s.key} options={offerOptions} disabled={!edit} onChange={v => this.setSlot({ key: v })} />
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
                        <TextField label="Start (UTC)" type="datetime-local" size="small" value={toUtcInput(s.start)} disabled={!edit} sx={{ width: 230 }}
                                   slotProps={{ inputLabel: { shrink: true } }} helperText={`now ${utcFull(new Date().toISOString())}`}
                                   onChange={e => this.setSlot({ start: fromUtcInput(e.target.value) })} />
                        <TextField label="End (UTC)" type="datetime-local" size="small" value={toUtcInput(s.end)} disabled={!edit} sx={{ width: 230 }}
                                   slotProps={{ inputLabel: { shrink: true } }} helperText={length}
                                   onChange={e => this.setSlot({ end: fromUtcInput(e.target.value) })} />
                        <FormControlLabel label="Enabled" disabled={!edit} sx={{ mt: 0.5 }}
                                          control={<Switch checked={!!s.enabled} onChange={e => this.setSlot({ enabled: e.target.checked })} />} />
                    </Box>
                    <PlatformPicker value={s.platforms} disabled={!edit} onChange={v => this.setSlot({ platforms: v })} />
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <SelectField label="Min version" size="small" minWidth={220} value={s.minVersion ?? 0} options={this.versionOptions("min", s.minVersion)} disabled={!edit}
                                     onChange={v => this.setSlot({ minVersion: v })} />
                        <SelectField label="Max version" size="small" minWidth={220} value={s.maxVersion ?? 0} options={this.versionOptions("max", s.maxVersion)} disabled={!edit}
                                     onChange={v => this.setSlot({ maxVersion: v })} />
                        {this.state.versionList.length === 0 && <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>No version list here: only “any”.</Typography>}
                    </Box>
                    <CountryPicker value={s.countries ?? []} onChange={v => (edit ? this.setSlot({ countries: v }) : null)} />
                    <TextField label="Note" size="small" fullWidth value={s.note ?? ""} disabled={!edit} onChange={e => this.setSlot({ note: e.target.value })} />
                    {!create && <Typography variant="caption" color="text.secondary">Slot {s.slotId} · updated {utcFull(s.updatedDate)} by {s.updatedBy || "?"}. Purchase limits count per slot: editing the window keeps them.</Typography>}
                </Box>
            </FormDialog>
        );
    }

    renderUploadDialog() {
        const { upload: u, branch } = this.state;
        if (!u) return null;
        const { check, result } = u;
        const blocked = !!check && (!check.hasOwn || check.missing.length > 0);
        const byPlatform = (a, b) => (a.platform ?? "").localeCompare(b.platform ?? "") || (a.name ?? "").localeCompare(b.name ?? "");
        const reg = result?.registered;
        const rows = reg ? [...(reg.bundles ?? [])].map(b => ({ ...b, status: uploadStatus(b) })).sort(byPlatform) : [];
        const counts = rows.reduce((acc, r) => { acc[r.status] = (acc[r.status] ?? 0) + 1; return acc; }, {});
        return (
            <FormDialog open title={result ? `Bundle uploaded · ${u.offerId}` : `Upload the bundle of ${u.offerId} · ${branch}`}
                        onClose={() => this.setState({ upload: null })} maxWidth={false} PaperProps={{ sx: { width: 900, maxWidth: "95vw" } }}
                        confirmText="Upload" onConfirm={this.uploadZip} hideConfirm={!!result} cancelText={result ? "Close" : "Cancel"}
                        confirmDisabled={!u.file || u.busy || blocked} cancelDisabled={u.busy}>
                {!result && <>
                    <Typography variant="body2" color="text.secondary">
                        The zip Unity's LiveOpsBundleBuild makes of Assets/LiveOps/{u.offerId}: every platform's bundles, registered on {branch} under the
                        owner offer:{u.offerId}; the offer then points at liveops/{u.offerId}. Players re-read the offer at their next sync.
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center", mt: 2 }}>
                        <Button variant="outlined" component="label" disabled={u.busy}>
                            Choose zip
                            <input type="file" accept=".zip" hidden onChange={this.pickZip} />
                        </Button>
                        {u.file && <Typography variant="body2" sx={ellipsis}>{u.file.name} · {formatSize(u.file.size)}</Typography>}
                    </Box>
                    {u.file && !check && !u.checkError && <LinearProgress sx={{ mt: 2 }} />}
                    {u.checkError && <Alert severity="warning" sx={{ mt: 2 }}>Could not read the zip here: {u.checkError} The server checks it again.</Alert>}
                    {check && !check.hasOwn && <Alert severity="error" sx={{ mt: 2 }}>The zip carries no '{check.expected}' bundle (it has {check.names.join(", ")}): build it from Assets/LiveOps/{u.offerId}.</Alert>}
                    {check && check.missing.length > 0 && <Alert severity="error" sx={{ mt: 2 }}>Files missing from the zip: {check.missing.join(", ")}</Alert>}
                    {check && check.owner && check.owner !== `offer:${u.offerId}` &&
                        <Alert severity="info" sx={{ mt: 2 }}>The manifest names the owner {check.owner}; this upload registers it as offer:{u.offerId}.</Alert>}
                    {check && <>
                        <Typography variant="body2" sx={{ mt: 2 }}>
                            owner {check.owner || "?"}{check.unity ? ` · Unity ${check.unity}` : ""}{check.changeset ? ` · cs:${check.changeset}` : ""}
                            {" · "}{check.bundles.length} bundles{check.platforms.length ? ` · ${check.expected} for ${check.platforms.join(", ")}` : ""}
                        </Typography>
                        <Scroll>
                            <DataTable sx={FIXED} rows={[...check.bundles].sort(byPlatform)} getRowKey={(b, i) => `${b.platform}/${b.name}/${i}`} columns={[
                                { id: "platform", label: "Platform", width: 90 },
                                { id: "name", label: "Name", width: 300, sx: ellipsis, render: b => <span title={b.name} style={b.name === check.expected ? { fontWeight: 600 } : undefined}>{b.name}</span> },
                                { id: "size", label: "Size", width: 90, align: "right", render: b => formatSize(b.size) },
                                { id: "deps", label: "Deps", width: 330, sx: ellipsis, render: b => { const t = (b.deps ?? []).join(", "); return <span title={t}>{t}</span>; } },
                            ]} />
                        </Scroll>
                    </>}
                </>}
                {result && <>
                    <Typography variant="body2">
                        {result.bundle} · owner {reg?.owner ?? "?"} · {Object.entries(counts).map(([k, n]) => `${n} ${UPLOAD_STATUS[k].label}`).join(" · ") || "no bundles"}
                        {" · "}seq {Object.entries(reg?.seq ?? {}).map(([p, n]) => `${p} ${n}`).join(", ") || "-"}
                    </Typography>
                    <Scroll>
                        <DataTable sx={FIXED} rows={rows} getRowKey={(b, i) => `${b.platform}/${b.name}/${i}`} columns={[
                            { id: "platform", label: "Platform", width: 90 },
                            { id: "name", label: "Name", width: 240, sx: ellipsis, render: b => <span title={b.name}>{b.name}</span> },
                            { id: "status", label: "", width: 110, render: b => <Chip size="small" variant="outlined" color={UPLOAD_STATUS[b.status].color} label={UPLOAD_STATUS[b.status].label} /> },
                            { id: "version", label: "Ver", width: 60, align: "right" },
                            { id: "size", label: "Size", width: 90, align: "right", render: b => formatSize(b.size) },
                            { id: "oldSize", label: "Was", width: 90, align: "right", render: b => (b.oldSize ? formatSize(b.oldSize) : "") },
                            { id: "error", label: "Error", width: 150, sx: ellipsis, render: b => <span title={b.error ?? ""}>{b.error ?? ""}</span> },
                        ]} />
                    </Scroll>
                </>}
                {u.busy && <LinearProgress sx={{ mt: 2 }} />}
                {u.error && <Alert severity="error" sx={{ mt: 2 }}>{u.error}</Alert>}
            </FormDialog>
        );
    }

    renderConfirmDialog() {
        const { confirm: c, branch } = this.state;
        if (!c) return null;
        return (
            <FormDialog open title={c.kind === "offer" ? `Delete offer ${c.id}?` : "Delete this slot?"} onClose={() => this.setState({ confirm: null })}
                        confirmText="Delete" onConfirm={this.confirmDelete} confirmDisabled={c.busy} cancelDisabled={c.busy}>
                <Typography variant="body2">
                    {c.kind === "offer"
                        ? `Removes ${c.id} from ${branch} and its ${c.slots ?? 0} slot${c.slots === 1 ? "" : "s"} from the schedule. The purchases made under it stay in the ledger.`
                        : `${c.label} on ${branch}: players stop seeing it at their next sync. The purchases made under it stay in the ledger.`}
                </Typography>
                {c.busy && <LinearProgress sx={{ mt: 2 }} />}
                {c.error && <Alert severity="error" sx={{ mt: 2 }}>{c.error}</Alert>}
            </FormDialog>
        );
    }

    renderPreviewDialog() {
        const { preview: p, branch, versionList, data } = this.state;
        if (!p) return null;
        const versions = clientPatchesOf(versionList, branch);
        const compact = this.previewCompact();
        const r = p.result, visible = r?.visible ?? [];
        const titles = new Map((data?.offers ?? []).map(o => [o.offerId, o.title]));
        const set = (field) => (value) => this.setState(s => ({ preview: { ...s.preview, [field]: value } }));
        return (
            <FormDialog open title={`Preview for a player · ${branch}`} onClose={() => this.setState({ preview: null })}
                        maxWidth={false} PaperProps={{ sx: { width: 980, maxWidth: "95vw" } }} cancelText="Close"
                        confirmText="Preview" onConfirm={this.runPreview} confirmDisabled={compact == null || p.busy}>
                <Typography variant="body2" color="text.secondary">
                    The slots a player with these facts is told about: live ones and those starting within 7 days, targeting applied
                    (purchase limits are not). Empty time = now.
                </Typography>
                <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", mt: 2, flexWrap: "wrap" }}>
                    <SelectField label="Platform" size="small" minWidth={130} value={p.platform} options={globals.platformNames} onChange={set("platform")} />
                    {versions.length > 0 && <SelectField label="Client version" size="small" minWidth={150} value={p.client} options={versions}
                                                         onChange={set("client")} disabled={!!p.clientText.trim()} />}
                    <TextField size="small" label={versions.length ? "or type M.m.p" : "Client version (M.m.p)"} sx={{ width: 170 }}
                               value={p.clientText} onChange={e => set("clientText")(e.target.value)} error={!!p.clientText.trim() && compact == null} />
                    <Autocomplete size="small" options={countryOptions()} value={p.country || null} sx={{ width: 240 }}
                                  onChange={(e, v) => set("country")(v ?? "")} getOptionLabel={c => `${countryName(c)} (${c})`}
                                  renderInput={params => <TextField {...params} label="Country" placeholder="none" />} />
                    <TextField label="At (UTC)" type="datetime-local" size="small" value={p.at} sx={{ width: 220 }}
                               slotProps={{ inputLabel: { shrink: true } }} onChange={e => set("at")(e.target.value)} />
                </Box>
                {p.busy && <LinearProgress sx={{ mt: 2 }} />}
                {p.error && <Alert severity="error" sx={{ mt: 2 }}>{p.error}</Alert>}
                {r && <>
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        At {utcFull(r.at)}, {p.platform} {verLabel(r.compact)}{p.country ? ` in ${countryName(p.country)}` : ""}: {visible.length} slot{visible.length === 1 ? "" : "s"}
                    </Typography>
                    {visible.length > 0 && (
                        <DataTable sx={{ ...FIXED, mt: 1 }} rows={visible} getRowKey={v => v.slot.slotId} columns={[
                            { id: "status", label: "", width: 100, render: v => <SlotChip status={v.live ? "live" : "upcoming"} /> },
                            { id: "offer", label: "Offer", width: 200, sx: ellipsis, render: v => <span title={titles.get(v.offer) ?? ""} style={mono}>{v.offer ?? v.slot.key}</span> },
                            { id: "window", label: "Window (UTC)", width: 230, render: v => `${utc(v.slot.start)} → ${utc(v.slot.end)}` },
                            { id: "versions", label: "Versions", width: 150, sx: ellipsis, render: v => verRange(v.slot.minVersion, v.slot.maxVersion) },
                            { id: "note", label: "Note", width: 220, sx: ellipsis, render: v => <span title={v.slot.note ?? ""}>{v.slot.note ?? ""}</span> },
                        ]} />
                    )}
                </>}
            </FormDialog>
        );
    }

    renderPurchasesDialog() {
        const { purchases: pu } = this.state;
        if (!pu) return null;
        const s = pu.slot, rows = pu.rows;
        const openPlayer = (id) => navigate({ title: globals.selectedTitle, page: "home", sub: "players", deep: [id] });
        return (
            <FormDialog open title={`Purchases · ${s.key} · ${utc(s.start)} → ${utc(s.end)} UTC`} onClose={() => this.setState({ purchases: null })}
                        hideConfirm cancelText="Close" maxWidth={false} PaperProps={{ sx: { width: 940, maxWidth: "95vw" } }}>
                {rows == null && <LinearProgress />}
                {pu.error && <Alert severity="error" sx={{ mb: 1 }}>{pu.error}</Alert>}
                {rows && rows.length === 0 && !pu.error && <Typography variant="body2">No purchases under this slot.</Typography>}
                {rows && rows.length > 0 && <>
                    <Typography variant="body2">{rows.length} purchase{rows.length === 1 ? "" : "s"}{rows.length >= 500 ? " (the newest 500)" : ""}</Typography>
                    <Scroll height={480}>
                        <DataTable sx={FIXED} rows={rows} getRowKey={(p, i) => `${p.grantId || ""}/${i}`} columns={[
                            { id: "player", label: "Player", width: 130, render: p => {
                                const id = typeof p.playerId === "string" ? p.playerId : null;
                                return id ? <Link component="button" variant="body2" sx={mono} title={id} onClick={() => openPlayer(id)}>…{id.slice(-8)}</Link>
                                          : <span title={JSON.stringify(p.playerId)}>—</span>;
                            }},
                            { id: "productId", label: "Product", width: 280, sx: ellipsis, render: p => <span title={p.productId} style={mono}>{p.productId}</span> },
                            { id: "state", label: "State", width: 110, render: p => <Chip size="small" variant="outlined" color={PURCHASE_STATE[String(p.state).toLowerCase()] ?? "default"} label={p.state} /> },
                            { id: "createdDate", label: "Date (UTC)", width: 150, render: p => utcFull(p.createdDate).replace(" UTC", "") },
                            { id: "grantId", label: "Grant", width: 200, sx: ellipsis, render: p => <span title={p.grantId} style={mono}>{p.grantId || "—"}</span> },
                        ]} />
                    </Scroll>
                </>}
            </FormDialog>
        );
    }

    render() {
        const { tab, branch, data, loading, error } = this.state;
        const edit = canEdit();
        const offers = data?.offers ?? [], slots = data?.slots ?? [];
        const filter = this.state.filter !== ALL && offers.some(o => o.offerId === this.state.filter) ? this.state.filter : ALL;
        const now = Date.now();
        const count = (st) => slots.filter(s => slotStatus(s, now) === st).length;
        return (
            <PageContainer maxWidth={1440}>
                <SectionHeader title="Offers">
                    {data && <Tooltip title="The branch's offers stamp: it grows on every write, and players re-read their offers when it moves">
                        <Chip size="small" label={`stamp ${data.stamp}`} />
                    </Tooltip>}
                    {!edit && <Chip size="small" label="read only" />}
                    <IconButton size="small" onClick={() => this.load()} disabled={loading}><RefreshIcon fontSize="small" /></IconButton>
                    <Button variant="outlined" size="small" onClick={this.openPreview}>Preview for a player</Button>
                    {edit && tab === "definitions" && <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={this.openNewOffer}>New offer</Button>}
                    {edit && tab === "schedule" && <Button variant="contained" size="small" startIcon={<AddIcon />} disabled={offers.length === 0} onClick={() => this.openNewSlot()}>New slot</Button>}
                </SectionHeader>
                <Tabs value={tab} onChange={this.setTab} sx={{ mb: 2 }}>
                    {TABS.map(([k, l]) => <Tab key={k} value={k} label={l} />)}
                </Tabs>
                <SelectorBar sx={{ alignItems: "center" }}>
                    <SelectField label="Branch" size="small" minWidth={160} value={branch} options={globals.branches} onChange={this.changeBranch} />
                    {tab === "schedule" && <SelectField label="Offer" size="small" minWidth={200} value={filter}
                                                        options={[{ value: ALL, label: "All offers" }, ...offers.map(o => o.offerId)]} onChange={f => this.setState({ filter: f })} />}
                    {data && (
                        <Typography variant="body2" color="text.secondary" sx={{ ml: "auto", textAlign: "right" }}>
                            {offers.length} offer{offers.length === 1 ? "" : "s"} · {slots.length} slot{slots.length === 1 ? "" : "s"}: {count("live")} live, {count("upcoming")} upcoming
                            <br />Times are UTC. Players see a change at their next session sync.
                        </Typography>
                    )}
                </SelectorBar>
                {loading && <LinearProgress sx={{ mb: 1 }} />}
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {tab === "definitions" ? this.renderDefinitions() : this.renderSchedule(filter)}
                {this.renderOfferDialog()}
                {this.renderSlotDialog()}
                {this.renderUploadDialog()}
                {this.renderConfirmDialog()}
                {this.renderPreviewDialog()}
                {this.renderPurchasesDialog()}
            </PageContainer>
        );
    }
}
